import Action from '../base/action';
import LoadTimeline from "../base/load-timeline";
import {TriggerTypeEnum} from "../base/training-literals";

class AlignWithPhysical extends Action {
    alignmentType;
    movementAnchor;
    rotationAnchor;
    centerAnchor;
    timelines;
    markerImage;
    backupTimelines;

    constructor(props) {
        super(props);
        this.alignmentType = props.alignmentType;
        this.movementAnchor = props.movementAnchor || {};
        this.rotationAnchor = props.rotationAnchor || {};
        this.centerAnchor = props.centerAnchor || {};
        this.timelines = new LoadTimeline({
            timelines: props.timelines || [],
            events: props.events || [],
            triggerType: props.triggerType || TriggerTypeEnum.TIMELINE
        });
        this.markerImage = props.markerImage || '';
        this.backupTimelines = new LoadTimeline({
            timelines: props.backupTimelines || [],
            events: props.backupEvents || [],
            triggerType: props.backupTriggerType || TriggerTypeEnum.TIMELINE
        });
    }

    async execute(eventLoop) {
        new Promise(async resolve => {
            if (!ModelAlignController.isValidOperation()) {
                return await this.loadTimeline(eventLoop);
            }

            const enteredAR = await ModelAlignController.enterAR(false);
            if (!enteredAR) {
                VRMessageController.addMessage({text: "Failed to enter AR. Skipping alignment"});
                return await this.loadTimeline(eventLoop);
            }

            const training = eventLoop.training;

            switch (this.alignmentType) {
                case AlignmentTypeEnum.TWO_POINTS:
                    await this.performPositionAlignment(training);
                    await this.performRotationAlignment(training);
                    break;
                case AlignmentTypeEnum.MARKER_BASED:
                    await this.performMarkerAlignment(eventLoop);
                    return;
                case AlignmentTypeEnum.PLACE_ON_SURFACE:
                    const {point, modelId} = this.getAnchorData(training, this.centerAnchor);
                    await ModelAlignController.startFloorAlign(true, modelId, point);
                    break;
            }

            await this.loadTimeline(eventLoop);
        });
    }

    getAnchorData(training, anchor) {
        const point = new THREE.Vector3(anchor.point.x, anchor.point.y, anchor.point.z);
        const modelId = training.resources[anchor.model].type === ResourceTypeEnum.HELPER_OBJECT
            ? training.resources[anchor.model].id
            : training.resources[anchor.model].resourceId;
        getModelObject(modelId).localToWorld(point);
        return {
            point,
            modelId
        }
    }

    async performMarkerAlignment(eventLoop) {
        if (isMobileDevice() && !isIOSDevice()) {
            VRMessageController.addMessage({
                text: "Aim the device at the marker to auto-align the virtual content",
                duration: 3000
            });
        } else if (iq3XRSession.isSessionRunning() && iq3XRSession.mode.includes(SessionMode.AR)) {
            VRMessageController.addMessage({
                text: "Marker alignment only works in mobile android devices.",
                duration: 3000
            });

            await this.handleMarkerAlignmentFailure(eventLoop);
        }

        try {
            await this.executeMarkerAlignment();
            return await this.loadTimeline(eventLoop);
        } catch (error) {
            console.warn('Marker alignment failed:', error.message);
            await this.handleMarkerAlignmentFailure(eventLoop);
        }
    }

    async executeMarkerAlignment() {
        if (!this.markerImage || !(this.markerImage in markerNameToIndex)) {
            const availableMarkers = Object.keys(markerNameToIndex).join(', ');
            const errorMessage = `Invalid marker configured: ${this.markerImage || 'undefined'}. Available markers: ${availableMarkers}`;
            console.error(errorMessage);
            VRMessageController.addMessage({
                text: `Invalid marker configuration. Please select a valid marker.`,
                duration: 5000
            });
            throw new Error(errorMessage);
        }

        let modelToAlign = modelManager._modelList.find(model => model.name === this.markerImage);
        modelToAlign = modelToAlign ? getModelObject(modelToAlign.id) : null;
        if (!modelToAlign || !modelToAlign.visible) {
            const errorMessage = `Virtual marker model "${this.markerImage}" not found in the scene. Alignment cannot be done.`;
            console.error(errorMessage);
            VRMessageController.addMessage({ text: errorMessage, duration: 5000 });
            throw new Error(errorMessage);
        }

        return new Promise((resolve, reject) => {
            const alignmentConfig = this.createAlignmentConfig(resolve, reject);
            XRHitTest.startMarkerAlignment(alignmentConfig);
        });
    }

    createAlignmentConfig(onSuccess, onFailure) {
        return {
            markerImage: this.markerImage,
            alignRotation: MARKER_ALIGNMENT_CONFIG.ALIGN_ROTATION,
            timeout: MARKER_ALIGNMENT_CONFIG.TIMEOUT_MS,
            useDepthCorrection: true, // Enable depth correction
            depthSamples: 9, // Number of depth samples
            onSuccess: () => this.handleMarkerAlignmentSuccess(onSuccess),
            onFailure: () => this.handleMarkerAlignmentTimeout(onFailure)
        };
    }

    handleMarkerAlignmentSuccess(resolve) {
        VRMessageController.addMessage({
            text: "Marker alignment successful!"
        });
        resolve(true);
    }

    handleMarkerAlignmentTimeout(reject) {
        VRMessageController.addMessage({
            text: "Marker detection timed out."
        });
        reject(new Error("Marker detection timed out"));
    }

    async handleMarkerAlignmentFailure(eventLoop) {
        if (this.backupTimelines) {
            return await this.backupTimelines.execute(eventLoop);
        } else {
            VRMessageController.addMessage({
                text: "Marker Alignment Failed."
            });
        }
    }

    async performPositionAlignment(training) {
        if (this.movementAnchor.model) {
            const {point, modelId} = this.getAnchorData(training, this.movementAnchor);
            anchorPointController.showAnchorPoint([
                AnchorTypeEnum.MOVEMENT_ANCHOR,
                modelId,
                this.movementAnchor.point,
                true
            ]);
            await ModelAlignController.startPositionAlignmentPhysicalPick({
                point: point
            });
        } else {
            await ModelAlignController.startPositionAlignmentVirtualPick(true, true);
        }

        anchorPointController.showAnchorPoint([
            AnchorTypeEnum.MOVEMENT_ANCHOR,
            "",
            {x: 0, y: 0, z: 0},
            false
        ]);
    }

    async performRotationAlignment(training) {
        if (this.rotationAnchor.model) {
            const {point, modelId} = this.getAnchorData(training, this.rotationAnchor);
            anchorPointController.showAnchorPoint([
                AnchorTypeEnum.ROTATION_ANCHOR,
                modelId,
                this.rotationAnchor.point,
                true
            ]);
            await ModelAlignController.startRotationAlignmentPhysicalPick({
                point: point,
                modelId: modelId
            });
        } else {
            await ModelAlignController.startRotationAlignmentVirtualPick(true, true);
        }

        anchorPointController.showAnchorPoint([
            AnchorTypeEnum.ROTATION_ANCHOR,
            "",
            {x: 0, y: 0, z: 0},
            false
        ]);
    }

    async loadTimeline(eventLoop) {
        if (this.timelines) {
            await this.timelines.execute(eventLoop);
        }
    }
}

export default AlignWithPhysical;
